import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformConfigAPI, filePublicUrl } from '../../services/api';
import {
  HiOutlineDocumentText,
  HiOutlineUpload,
  HiOutlineTrash,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlinePhotograph,
  HiOutlineOfficeBuilding,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineGlobe,
  HiOutlineLocationMarker,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

const emptyConfig = {
  companyName: 'TeleValley',
  tagline: 'Tax Invoice / Receipt',
  gstin: '',
  phone: '',
  email: '',
  website: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  country: 'India',
  zipCode: '',
  logo: '',
  logoFileName: '',
};

const InvoiceSettings = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyConfig);

  // Fetch current config
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['platform-config'],
    queryFn: async () => {
      const res = await platformConfigAPI.getConfig();
      return res.data?.data || {};
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (data) {
      setForm({
        companyName: data.companyName || '',
        tagline: data.tagline || '',
        gstin: data.gstin || '',
        phone: data.phone || '',
        email: data.email || '',
        website: data.website || '',
        addressLine1: data.addressLine1 || '',
        addressLine2: data.addressLine2 || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || 'India',
        zipCode: data.zipCode || '',
        logo: data.logo || '',
        logoFileName: data.logoFileName || '',
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        companyName: form.companyName,
        tagline: form.tagline,
        gstin: form.gstin,
        phone: form.phone,
        email: form.email,
        website: form.website,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state,
        country: form.country,
        zipCode: form.zipCode,
      };
      await platformConfigAPI.updateConfig(payload);
      toast.success('Invoice settings saved successfully');
      refetch();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PNG, JPEG, WebP, and SVG images are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await platformConfigAPI.uploadLogo(formData);
      toast.success('Logo uploaded successfully');
      refetch();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the logo?')) return;
    try {
      await platformConfigAPI.removeLogo();
      toast.success('Logo removed');
      refetch();
    } catch (err) {
      console.error('Remove logo error:', err);
      toast.error(err.response?.data?.message || 'Failed to remove logo');
    }
  };

  const logoUrl = form.logo ? filePublicUrl(`/uploads/${form.logo}`) : '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
            <HiOutlineDocumentText className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Invoice Settings</h2>
            <p className="text-sm text-gray-500">Customize your invoice branding and business details</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 rounded-lg hover:bg-secondary-100 text-secondary-500 hover:text-secondary-700 transition-colors"
          title="Refresh"
        >
          <HiOutlineRefresh className="w-5 h-5" />
        </button>
      </div>

      {/* Logo Upload Section */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-gradient-to-r from-primary-50 to-white">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <HiOutlinePhotograph className="w-5 h-5 text-primary-500" />
            Company Logo
          </h3>
          <p className="text-xs text-secondary-500 mt-0.5">
            This logo appears in invoice headers. A white background is added automatically. Recommended: 200×60px PNG or SVG with transparent or dark background.
          </p>
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Logo Preview */}
            <div className="w-48 h-20 rounded-xl border-2 border-dashed border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company logo"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <HiOutlinePhotograph className="w-8 h-8 mx-auto" />
                  <p className="text-xs mt-1">No logo</p>
                </div>
              )}
            </div>
            {/* Upload / Remove actions */}
            <div className="flex-1 space-y-3">
              <div>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-600 text-white font-medium text-sm hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {uploading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <HiOutlineUpload className="w-4 h-4" />
                      Upload Logo
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              {form.logo && (
                <button
                  onClick={handleRemoveLogo}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <HiOutlineTrash className="w-4 h-4" />
                  Remove Logo
                </button>
              )}
              {form.logoFileName && (
                <p className="text-xs text-gray-500">
                  Current: <span className="font-medium text-gray-700">{form.logoFileName}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Company Details Form */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-gradient-to-r from-primary-50 to-white">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <HiOutlineOfficeBuilding className="w-5 h-5 text-primary-500" />
            Business Details
          </h3>
          <p className="text-xs text-secondary-500 mt-0.5">
            These details appear in the invoice header and "From" section.
          </p>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                Tagline <span className="text-secondary-400 font-normal">(invoice header subtitle)</span>
              </label>
              <input
                type="text"
                value={form.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Tax Invoice / Receipt"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                GSTIN
              </label>
              <input
                type="text"
                value={form.gstin}
                onChange={(e) => handleChange('gstin', e.target.value)}
                placeholder="e.g., 27AABCU9603R1ZM"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Address Section — commented out, not needed for "From" section yet */}
      {/* Address Section */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-gradient-to-r from-primary-50 to-white">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <HiOutlineLocationMarker className="w-5 h-5 text-primary-500" />
            Business Address
          </h3>
          <p className="text-xs text-secondary-500 mt-0.5">
            This address appears in the "From" section of invoices.
          </p>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
              Address Line 1
            </label>
            <input
              type="text"
              value={form.addressLine1}
              onChange={(e) => handleChange('addressLine1', e.target.value)}
              placeholder="e.g., 123 Business Park, Sector 5"
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
              Address Line 2
            </label>
            <input
              type="text"
              value={form.addressLine2}
              onChange={(e) => handleChange('addressLine2', e.target.value)}
              placeholder="e.g., Tower B, 4th Floor"
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                City
              </label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="e.g., Mumbai"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                State
              </label>
              <input
                type="text"
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
                placeholder="e.g., Maharashtra"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                ZIP Code
              </label>
              <input
                type="text"
                value={form.zipCode}
                onChange={(e) => handleChange('zipCode', e.target.value)}
                placeholder="e.g., 400001"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
              Country
            </label>
            <input
              type="text"
              value={form.country}
              onChange={(e) => handleChange('country', e.target.value)}
              placeholder="e.g., India"
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
            />
          </div>
        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-gradient-to-r from-primary-50 to-white">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <HiOutlinePhone className="w-5 h-5 text-primary-500" />
            Contact Details
          </h3>
          <p className="text-xs text-secondary-500 mt-0.5">
            Phone, email, and website shown on invoices.
          </p>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                <HiOutlinePhone className="w-4 h-4 inline mr-1 text-gray-400" />
                Phone
              </label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="e.g., +91 98765 43210"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                <HiOutlineMail className="w-4 h-4 inline mr-1 text-gray-400" />
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="e.g., billing@televalley.in"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">
                <HiOutlineGlobe className="w-4 h-4 inline mr-1 text-gray-400" />
                Website
              </label>
              <input
                type="text"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="e.g., https://televalley.in"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Preview */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 bg-gradient-to-r from-primary-50 to-white">
          <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
            <HiOutlineDocumentText className="w-5 h-5 text-primary-500" />
            Invoice Preview
          </h3>
          <p className="text-xs text-secondary-500 mt-0.5">
            Preview of how your invoice header and "From" section will look. "Bill To" is auto-filled from each organization's data.
          </p>
        </div>
        <div className="p-6">
          {/* Mini invoice preview */}
          <div className="rounded-xl border border-gray-200 overflow-hidden max-w-2xl mx-auto">
            {/* Header */}
            <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
              <div className="flex items-center">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-10 max-w-[140px] object-contain bg-white rounded-lg px-2 py-1" />
                ) : (
                  <span className="text-xl font-bold">{form.companyName || 'TeleValley'}</span>
                )}
              </div>
              <div className="text-right text-xs opacity-80">
                <p><strong>Invoice No:</strong> TV-DEMO1</p>
                <p><strong>Date:</strong> 01 Jan 2026</p>
              </div>
            </div>
            {/* From section only — Bill To comes from org data */}
            <div className="px-6 py-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">From</p>
              <p className="text-sm font-semibold text-gray-900">{form.companyName || 'TeleValley'}</p>
              {form.gstin && <p className="text-sm text-gray-600">GSTIN: {form.gstin}</p>}
              {form.addressLine1 && <p className="text-sm text-gray-600">{form.addressLine1}</p>}
              {form.addressLine2 && <p className="text-sm text-gray-600">{form.addressLine2}</p>}
              {[form.city, form.state, form.zipCode].filter(Boolean).join(', ') && (
                <p className="text-sm text-gray-600">
                  {[form.city, form.state, form.zipCode].filter(Boolean).join(', ')}
                </p>
              )}
              {form.country && <p className="text-sm text-gray-600">{form.country}</p>}
              {form.phone && <p className="text-sm text-gray-600">Phone: {form.phone}</p>}
              {form.email && <p className="text-sm text-gray-600">{form.email}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 text-white font-medium text-sm hover:bg-primary-700 focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Saving...
            </>
          ) : (
            <>
              <HiOutlineCheckCircle className="w-4 h-4" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default InvoiceSettings;